import { format, parseISO } from 'date-fns'
import { useTranslate } from '@ur-solutions/react-hooks'
import styled from 'styled-components'
import { getWorkPeriodDayExtensionState } from '../work-period-day-extension'
import {
  shortenLocationDetail,
  type LocationDetailInfo,
} from '../utils/shorten-location-detail'

type TimeEntry = {
  datetimeStart: string
  datetimeEnd: string | null
}

const TimeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  width: 100%;
`

const TimeBlock = styled.div`
  min-width: 0;
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background: ${props => props.theme.colors.white};
`

const PlannedTime = styled.div`
  min-height: 1rem;
  color: ${props => props.theme.colors.gray3};
  font-size: 0.8rem;
  line-height: 1.2;
`

const ActualTime = styled.div`
  margin-top: 0.2rem;
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1;
`

const ExtensionNote = styled.div`
  margin-top: 0.2rem;
  color: ${props => props.theme.colors.primary500};
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.2;
`

const LocationDetail = styled.div`
  margin-top: 0.35rem;
  color: ${props => props.theme.colors.gray3};
  font-size: 0.8rem;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
`

/**
 * Start/end time presentation matching the approval cards: large actual times,
 * planned times from the connected calendar item, and "added before/after"
 * notes when time entries extend the work period day.
 */
export const WorkPeriodTimeBlocks: React.FC<{
  datetimeStart: string
  datetimeEnd: string
  plannedStart?: string | null
  plannedEnd?: string | null
  startLocation?: LocationDetailInfo | null
  endLocation?: LocationDetailInfo | null
  timeEntries: TimeEntry[]
}> = ({
  datetimeStart,
  datetimeEnd,
  plannedStart,
  plannedEnd,
  startLocation,
  endLocation,
  timeEntries,
}) => {
  const translations = useTranslate({
    planned: 'common.planned',
    addedBefore: 'salary.extend-work-period-added-before',
    addedAfter: 'salary.extend-work-period-added-after',
  })

  const start = parseISO(datetimeStart)
  const end = parseISO(datetimeEnd)
  const extensionState = getWorkPeriodDayExtensionState({
    workPeriodStart: start,
    workPeriodEnd: end,
    timeEntries,
  })

  const plannedStartLabel = plannedStart
    ? format(parseISO(plannedStart), 'HH:mm')
    : null
  const plannedEndLabel = plannedEnd
    ? format(parseISO(plannedEnd), 'HH:mm')
    : null

  const startLocationLabel = shortenLocationDetail(startLocation)
  const endLocationLabel = shortenLocationDetail(endLocation)

  return (
    <TimeGrid>
      <TimeBlock>
        {plannedStartLabel && (
          <PlannedTime>
            {translations.planned} {plannedStartLabel}
          </PlannedTime>
        )}
        <ActualTime>{format(start, 'HH:mm')}</ActualTime>
        {extensionState.beforeExtensionStart && (
          <ExtensionNote>
            {translations.addedBefore}{' '}
            {format(extensionState.beforeExtensionStart, 'HH:mm')}
          </ExtensionNote>
        )}
        {startLocationLabel && (
          <LocationDetail>{startLocationLabel}</LocationDetail>
        )}
      </TimeBlock>
      <TimeBlock>
        {plannedEndLabel && (
          <PlannedTime>
            {translations.planned} {plannedEndLabel}
          </PlannedTime>
        )}
        <ActualTime>{format(end, 'HH:mm')}</ActualTime>
        {extensionState.afterExtensionEnd && (
          <ExtensionNote>
            {translations.addedAfter}{' '}
            {format(extensionState.afterExtensionEnd, 'HH:mm')}
          </ExtensionNote>
        )}
        {endLocationLabel && (
          <LocationDetail>{endLocationLabel}</LocationDetail>
        )}
      </TimeBlock>
    </TimeGrid>
  )
}
